/**
 * useCancelSubscription Hook
 * Custom hook for canceling an active subscription
 */

import {useMutation, useQueryClient} from 'react-query';
import {CancelSubscriptionResponse} from '~types/subscription/subscription.types';
import {useUserStore} from '~providers/userStore/zustand';
import {BASE_API_URL} from '@env';

/**
 * Call the cancel subscription API
 */
async function cancelSubscriptionAPI(token?: string): Promise<CancelSubscriptionResponse> {
  const headers: Record<string, string> = {
    'accept': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `${token}`;
  }

  const response = await fetch(`${BASE_API_URL}stripe/subscription/cancel`, {
    method: 'POST',
    headers,
    body: '',
  });

  if (!response.ok) {
    throw new Error(`Failed to cancel subscription: ${response.status}`);
  }

  const data: CancelSubscriptionResponse = await response.json();
  return data;
}

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const token = useUserStore.getState().token;
      return cancelSubscriptionAPI(token);
    },
    {
      onSuccess: (data) => {
        // Invalidate the active plan query to refresh it
        queryClient.invalidateQueries(['activePlan']);

        // Optionally log the successful cancellation
        console.log('✅ Subscription canceled:', data.message);
      },
      onError: (error: Error) => {
        console.error('❌ Failed to cancel subscription:', error.message);
      },
    },
  );
};
